/**
 * CEO Command Center v11 - Sync Settings Panel
 *
 * Comprehensive settings for P2P sync:
 * - Identity management
 * - Space management (create, leave, invite)
 * - Peer connections
 * - Sync status and controls
 *
 * @module components/common/SyncSettings
 * @version 11.0.0
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useSyncStore } from '@/stores/syncStore';
import { transportService } from '@/services/sync';
import { cn } from '@/utils/format';
import type { Space, SpaceMember, DeviceIdentity } from '@/types/sync';
import { SpaceInviteDialog, JoinSpaceDialog } from './SpaceInvite';

// ============================================
// SYNC SETTINGS PANEL
// ============================================

interface SyncSettingsPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

export const SyncSettingsPanel: React.FC<SyncSettingsPanelProps> = ({
  isOpen,
  onClose,
}) => {
  const {
    identity,
    spaces,
    activeSpaceId,
    status,
    setActiveSpace,
    createSpace,
    leaveSpace,
    getSpaceMembers,
    createIdentity,
  } = useSyncStore();

  const [activeTab, setActiveTab] = useState<'identity' | 'spaces' | 'peers'>(identity ? 'spaces' : 'identity');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Space management state
  const [showCreateSpace, setShowCreateSpace] = useState(false);
  const [newSpaceName, setNewSpaceName] = useState('');
  const [inviteSpaceId, setInviteSpaceId] = useState<string | null>(null);
  const [showJoinSpace, setShowJoinSpace] = useState(false);
  const [spaceMembers, setSpaceMembers] = useState<Map<string, SpaceMember[]>>(new Map());

  // Peer connection state
  const [myPeerId, setMyPeerId] = useState<string | null>(null);
  const [remotePeerId, setRemotePeerId] = useState('');
  const [connecting, setConnecting] = useState(false);
  const [connectedPeerIds, setConnectedPeerIds] = useState<string[]>([]);

  // Load peer info
  useEffect(() => {
    if (isOpen) {
      setMyPeerId(transportService.getPeerId());
      setConnectedPeerIds(transportService.getConnectedPeers());
    }
  }, [isOpen, status]);

  // Load space members when spaces tab is active
  useEffect(() => {
    if (isOpen && activeTab === 'spaces') {
      loadAllSpaceMembers();
    }
  }, [isOpen, activeTab, spaces]);

  const loadAllSpaceMembers = async () => {
    const membersMap = new Map<string, SpaceMember[]>();
    for (const space of spaces) {
      try {
        const members = await getSpaceMembers(space.id);
        membersMap.set(space.id, members);
      } catch {
        // Ignore errors for individual spaces
      }
    }
    setSpaceMembers(membersMap);
  };

  const handleCreateSpace = async () => {
    if (!newSpaceName.trim()) return;

    setLoading(true);
    setError(null);

    try {
      await createSpace(newSpaceName.trim());
      setNewSpaceName('');
      setShowCreateSpace(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create space');
    } finally {
      setLoading(false);
    }
  };

  const handleLeaveSpace = async (spaceId: string) => {
    if (!confirm('Are you sure you want to leave this space? Your local data will remain.')) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await leaveSpace(spaceId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to leave space');
    } finally {
      setLoading(false);
    }
  };

  const handleConnectPeer = useCallback(async () => {
    if (!remotePeerId.trim()) return;

    setConnecting(true);
    setError(null);

    try {
      await transportService.connect(remotePeerId.trim());
      setRemotePeerId('');
      setConnectedPeerIds(transportService.getConnectedPeers());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to connect to peer');
    } finally {
      setConnecting(false);
    }
  }, [remotePeerId]);

  const handleDisconnectPeer = useCallback(async (peerId: string) => {
    try {
      await transportService.disconnect(peerId);
      setConnectedPeerIds(transportService.getConnectedPeers());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to disconnect');
    }
  }, []);

  const copyToClipboard = async (text: string) => {
    await navigator.clipboard.writeText(text);
  };

  if (!isOpen) return null;

  const inviteSpace = inviteSpaceId ? spaces.find(s => s.id === inviteSpaceId) : null;

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        {/* Backdrop */}
        <div className="fixed inset-0 bg-black/50" onClick={onClose} />

        {/* Panel */}
        <div className="relative bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[80vh] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b">
            <h2 className="text-lg font-semibold text-gray-900">Sync Settings</h2>
            <button
              onClick={onClose}
              className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Tabs */}
          <div className="flex border-b px-6">
            {(['identity', 'spaces', 'peers'] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={cn(
                  'px-4 py-3 text-sm font-medium transition-colors border-b-2 -mb-px',
                  activeTab === tab
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                )}
              >
                {tab === 'identity' && 'Identity'}
                {tab === 'spaces' && 'Spaces'}
                {tab === 'peers' && 'Peers'}
              </button>
            ))}
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {error && (
              <div className="mb-4 p-3 bg-red-50 text-red-700 text-sm rounded-lg">{error}</div>
            )}

            {/* Identity Tab */}
            {activeTab === 'identity' && (
              <IdentitySection identity={identity} onCreateIdentity={createIdentity} />
            )}

            {/* Spaces Tab */}
            {activeTab === 'spaces' && (
              <SpacesSection
                spaces={spaces}
                activeSpaceId={activeSpaceId}
                spaceMembers={spaceMembers}
                loading={loading}
                showCreateSpace={showCreateSpace}
                newSpaceName={newSpaceName}
                onSetActiveSpace={setActiveSpace}
                onLeaveSpace={handleLeaveSpace}
                onInvite={setInviteSpaceId}
                onShowCreateSpace={() => setShowCreateSpace(true)}
                onHideCreateSpace={() => setShowCreateSpace(false)}
                onNewSpaceNameChange={setNewSpaceName}
                onCreateSpace={handleCreateSpace}
                onJoinSpace={() => setShowJoinSpace(true)}
              />
            )}

            {/* Peers Tab */}
            {activeTab === 'peers' && (
              <PeersSection
                myPeerId={myPeerId}
                remotePeerId={remotePeerId}
                connecting={connecting}
                connectedPeerIds={connectedPeerIds}
                status={status}
                onRemotePeerIdChange={setRemotePeerId}
                onConnect={handleConnectPeer}
                onDisconnect={handleDisconnectPeer}
                onCopy={copyToClipboard}
              />
            )}
          </div>
        </div>
      </div>

      {/* Space Invite Dialog */}
      {inviteSpace && (
        <SpaceInviteDialog
          isOpen={!!inviteSpaceId}
          onClose={() => setInviteSpaceId(null)}
          spaceId={inviteSpace.id}
          spaceName={inviteSpace.name}
        />
      )}

      {/* Join Space Dialog */}
      <JoinSpaceDialog
        isOpen={showJoinSpace}
        onClose={() => setShowJoinSpace(false)}
        onSuccess={loadAllSpaceMembers}
      />
    </>
  );
};

// ============================================
// IDENTITY SECTION
// ============================================

interface IdentitySectionProps {
  identity: DeviceIdentity | null;
  onCreateIdentity?: (name: string, passphrase: string) => Promise<DeviceIdentity>;
}

const IdentitySection: React.FC<IdentitySectionProps> = ({
  identity,
  onCreateIdentity,
}) => {
  const [deviceName, setDeviceName] = useState('');
  const [passphrase, setPassphrase] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!deviceName.trim() || !passphrase.trim() || !onCreateIdentity) return;

    setLoading(true);
    setError(null);

    try {
      await onCreateIdentity(deviceName.trim(), passphrase);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create identity');
    } finally {
      setLoading(false);
    }
  };

  if (!identity) {
    return (
      <div className="space-y-4">
        <div className="text-center py-4">
          <div className="text-4xl mb-4">🔐</div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Create Your Identity</h3>
          <p className="text-sm text-gray-500 mb-4">
            Set up a local identity to enable P2P sync with SQLite storage.
          </p>
        </div>

        <form onSubmit={handleCreate} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Device Name</label>
            <input
              type="text"
              value={deviceName}
              onChange={(e) => setDeviceName(e.target.value)}
              placeholder="e.g., My Laptop"
              className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <p className="text-xs text-gray-500 mt-1">This name is shared with peers</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Passphrase</label>
            <input
              type="password"
              value={passphrase}
              onChange={(e) => setPassphrase(e.target.value)}
              placeholder="Enter a secure passphrase"
              className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <p className="text-xs text-gray-500 mt-1">Used to encrypt your identity keys</p>
          </div>

          {error && (
            <div className="p-3 bg-red-50 text-red-700 text-sm rounded-lg">{error}</div>
          )}

          <button
            type="submit"
            disabled={loading || !deviceName.trim() || !passphrase.trim()}
            className="w-full px-4 py-2 bg-blue-500 text-white text-sm font-medium rounded-lg hover:bg-blue-600 disabled:opacity-50 transition-colors"
          >
            {loading ? 'Creating...' : 'Create Identity & Enable P2P'}
          </button>
        </form>

        <div className="pt-4 border-t">
          <h4 className="text-sm font-medium text-gray-700 mb-2">What this does:</h4>
          <ul className="text-sm text-gray-500 space-y-1">
            <li>• Creates encrypted identity keys on this device</li>
            <li>• Switches from Airtable to local SQLite storage</li>
            <li>• Enables peer-to-peer sync with other devices</li>
          </ul>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Display Name</label>
        <p className="text-gray-900 bg-gray-50 px-3 py-2 rounded-lg">{identity.name}</p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Public Key</label>
        <p className="text-xs text-gray-600 bg-gray-50 px-3 py-2 rounded-lg font-mono break-all">
          {identity.publicKey}
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Created</label>
        <p className="text-xs text-gray-600 bg-gray-50 px-3 py-2 rounded-lg">
          {new Date(identity.createdAt).toLocaleDateString()}
        </p>
      </div>

      <div className="pt-4 border-t">
        <h4 className="text-sm font-medium text-gray-700 mb-2">About Your Identity</h4>
        <ul className="text-sm text-gray-500 space-y-1">
          <li>• Your identity is stored locally and encrypted</li>
          <li>• The public key is shared with peers you sync with</li>
          <li>• Each device has its own identity</li>
        </ul>
      </div>
    </div>
  );
};

// ============================================
// SPACES SECTION
// ============================================

interface SpacesSectionProps {
  spaces: Space[];
  activeSpaceId: string | null;
  spaceMembers: Map<string, SpaceMember[]>;
  loading: boolean;
  showCreateSpace: boolean;
  newSpaceName: string;
  onSetActiveSpace: (id: string) => void;
  onLeaveSpace: (id: string) => void;
  onInvite: (id: string) => void;
  onShowCreateSpace: () => void;
  onHideCreateSpace: () => void;
  onNewSpaceNameChange: (name: string) => void;
  onCreateSpace: () => void;
  onJoinSpace: () => void;
}

const SpacesSection: React.FC<SpacesSectionProps> = ({
  spaces,
  activeSpaceId,
  spaceMembers,
  loading,
  showCreateSpace,
  newSpaceName,
  onSetActiveSpace,
  onLeaveSpace,
  onInvite,
  onShowCreateSpace,
  onHideCreateSpace,
  onNewSpaceNameChange,
  onCreateSpace,
  onJoinSpace,
}) => {
  return (
    <div className="space-y-4">
      {/* Action Buttons */}
      <div className="flex gap-2">
        <button
          onClick={onShowCreateSpace}
          className="px-3 py-2 bg-blue-500 text-white text-sm font-medium rounded-lg hover:bg-blue-600 transition-colors"
        >
          + Create Space
        </button>
        <button
          onClick={onJoinSpace}
          className="px-3 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-200 transition-colors"
        >
          Join Space
        </button>
      </div>

      {/* Create Space Form */}
      {showCreateSpace && (
        <div className="p-4 bg-blue-50 rounded-lg">
          <h4 className="text-sm font-medium text-blue-900 mb-2">Create New Space</h4>
          <div className="flex gap-2">
            <input
              type="text"
              value={newSpaceName}
              onChange={(e) => onNewSpaceNameChange(e.target.value)}
              placeholder="Space name..."
              className="flex-1 px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <button
              onClick={onCreateSpace}
              disabled={loading || !newSpaceName.trim()}
              className="px-4 py-2 bg-blue-500 text-white text-sm font-medium rounded-lg hover:bg-blue-600 disabled:opacity-50 transition-colors"
            >
              {loading ? 'Creating...' : 'Create'}
            </button>
            <button
              onClick={onHideCreateSpace}
              className="px-3 py-2 text-gray-600 text-sm hover:bg-gray-100 rounded-lg transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Spaces List */}
      {spaces.length === 0 ? (
        <div className="text-center py-8">
          <div className="text-4xl mb-4">📦</div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Spaces</h3>
          <p className="text-sm text-gray-500">
            Create a space or join one to start syncing.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {spaces.map((space) => {
            const members = spaceMembers.get(space.id) || [];
            const isActive = space.id === activeSpaceId;

            return (
              <div
                key={space.id}
                className={cn(
                  'p-4 rounded-lg border transition-colors',
                  isActive ? 'border-blue-300 bg-blue-50' : 'border-gray-200 bg-white'
                )}
              >
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <h4 className="font-medium text-gray-900 flex items-center gap-2">
                      {space.name}
                      {isActive && (
                        <span className="text-xs bg-blue-500 text-white px-2 py-0.5 rounded-full">
                          Active
                        </span>
                      )}
                    </h4>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {members.length} member{members.length !== 1 ? 's' : ''} • {space.tables.length} tables
                    </p>
                  </div>
                  <div className="flex gap-1">
                    {!isActive && (
                      <button
                        onClick={() => onSetActiveSpace(space.id)}
                        className="px-2 py-1 text-xs text-blue-600 hover:bg-blue-100 rounded transition-colors"
                      >
                        Activate
                      </button>
                    )}
                    <button
                      onClick={() => onInvite(space.id)}
                      className="px-2 py-1 text-xs text-gray-600 hover:bg-gray-100 rounded transition-colors"
                    >
                      Invite
                    </button>
                    {spaces.length > 1 && (
                      <button
                        onClick={() => onLeaveSpace(space.id)}
                        className="px-2 py-1 text-xs text-red-600 hover:bg-red-100 rounded transition-colors"
                      >
                        Leave
                      </button>
                    )}
                  </div>
                </div>

                {/* Members */}
                {members.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-gray-100">
                    <p className="text-xs text-gray-500 mb-2">Members:</p>
                    <div className="flex flex-wrap gap-2">
                      {members.map((member) => (
                        <span
                          key={member.publicKey}
                          className={cn(
                            'text-xs px-2 py-1 rounded-full',
                            member.role === 'owner'
                              ? 'bg-amber-100 text-amber-800'
                              : 'bg-gray-100 text-gray-700'
                          )}
                        >
                          {member.name}
                          {member.role === 'owner' && ' (owner)'}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Tables */}
                <div className="mt-3 pt-3 border-t border-gray-100">
                  <p className="text-xs text-gray-500 mb-2">Synced tables:</p>
                  <p className="text-xs text-gray-600">
                    {space.tables.join(', ')}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ============================================
// PEERS SECTION
// ============================================

interface PeersSectionProps {
  myPeerId: string | null;
  remotePeerId: string;
  connecting: boolean;
  connectedPeerIds: string[];
  status: string;
  onRemotePeerIdChange: (id: string) => void;
  onConnect: () => void;
  onDisconnect: (id: string) => void;
  onCopy: (text: string) => void;
}

const PeersSection: React.FC<PeersSectionProps> = ({
  myPeerId,
  remotePeerId,
  connecting,
  connectedPeerIds,
  status,
  onRemotePeerIdChange,
  onConnect,
  onDisconnect,
  onCopy,
}) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    if (myPeerId) {
      await onCopy(myPeerId);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="space-y-6">
      {/* Status */}
      <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
        <div className={cn(
          'w-3 h-3 rounded-full',
          status === 'connected' ? 'bg-green-500' :
          status === 'connecting' ? 'bg-yellow-500 animate-pulse' :
          'bg-gray-400'
        )} />
        <div>
          <p className="text-sm font-medium text-gray-900">
            {status === 'connected' ? 'Connected' :
             status === 'connecting' ? 'Connecting...' :
             'Disconnected'}
          </p>
          <p className="text-xs text-gray-500">
            {connectedPeerIds.length} peer{connectedPeerIds.length !== 1 ? 's' : ''} connected
          </p>
        </div>
      </div>

      {/* My Peer ID */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Your Peer ID</label>
        <p className="text-xs text-gray-500 mb-2">Share this ID with others so they can connect to you</p>
        <div className="flex gap-2">
          <input
            type="text"
            readOnly
            value={myPeerId || 'Not initialized'}
            className="flex-1 px-3 py-2 bg-gray-50 border rounded-lg text-sm font-mono text-gray-600"
          />
          <button
            onClick={handleCopy}
            disabled={!myPeerId}
            className="px-4 py-2 bg-blue-500 text-white text-sm font-medium rounded-lg hover:bg-blue-600 disabled:opacity-50 transition-colors whitespace-nowrap"
          >
            {copied ? 'Copied!' : 'Copy'}
          </button>
        </div>
      </div>

      {/* Connect to Peer */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Connect to Peer</label>
        <p className="text-xs text-gray-500 mb-2">Enter another user's peer ID to connect</p>
        <div className="flex gap-2">
          <input
            type="text"
            value={remotePeerId}
            onChange={(e) => onRemotePeerIdChange(e.target.value)}
            placeholder="ceocc-abc123..."
            className="flex-1 px-3 py-2 border rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <button
            onClick={onConnect}
            disabled={connecting || !remotePeerId.trim()}
            className="px-4 py-2 bg-green-500 text-white text-sm font-medium rounded-lg hover:bg-green-600 disabled:opacity-50 transition-colors"
          >
            {connecting ? 'Connecting...' : 'Connect'}
          </button>
        </div>
      </div>

      {/* Connected Peers */}
      <div>
        <h4 className="text-sm font-medium text-gray-700 mb-2">Connected Peers</h4>
        {connectedPeerIds.length === 0 ? (
          <div className="p-4 bg-gray-50 rounded-lg text-center">
            <p className="text-sm text-gray-500">No peers connected</p>
            <p className="text-xs text-gray-400 mt-1">
              Share your peer ID or enter another peer's ID to connect
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {connectedPeerIds.map((peerId) => (
              <div
                key={peerId}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
              >
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-green-500" />
                  <span className="text-sm font-mono text-gray-600 truncate max-w-[300px]">
                    {peerId}
                  </span>
                </div>
                <button
                  onClick={() => onDisconnect(peerId)}
                  className="px-2 py-1 text-xs text-red-600 hover:bg-red-100 rounded transition-colors"
                >
                  Disconnect
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Info */}
      <div className="p-4 bg-blue-50 rounded-lg">
        <h4 className="text-sm font-medium text-blue-900 mb-2">How P2P Sync Works</h4>
        <ul className="text-xs text-blue-800 space-y-1">
          <li>• Changes sync directly between connected peers</li>
          <li>• Data is encrypted using your space key</li>
          <li>• No server stores your data - it's fully peer-to-peer</li>
          <li>• Peers must share at least one space to sync</li>
        </ul>
      </div>
    </div>
  );
};

export default SyncSettingsPanel;
